import type { ReactNode } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import { LogoOpenAI } from '@mc/educator-design-system'
import ClayLogo from './assets/partners/clay.svg'
import DecagonLogo from './assets/partners/decagon.svg'
import ElevenLabsLogo from './assets/partners/elevenlabs.svg'
import GammaLogo from './assets/partners/gamma.svg'
import GraphiteLogo from './assets/partners/graphite.svg'
import Micro1Logo from './assets/partners/micro1.svg'
import WandbLogo from './assets/partners/wandb.svg'
import { AI_LABS_PARTNERS } from './constants/aiLabsPartners'

interface AiLabsPartnersProps {
  /** Section-header title above the logo row. */
  headline: string
}

// One full marquee loop; adjustable.
const MARQUEE_DURATION_S = 24

const LOGO_CLASS = 'max-h-full max-w-full text-edu-fg-primary'

// Keyed by AI_LABS_PARTNERS id.
const LOGOS_BY_ID: Record<string, ReactNode> = {
  clay: <ClayLogo role='img' aria-label='Clay' className={LOGO_CLASS} />,
  decagon: (
    <DecagonLogo role='img' aria-label='Decagon' className={LOGO_CLASS} />
  ),
  elevenlabs: (
    <ElevenLabsLogo role='img' aria-label='ElevenLabs' className={LOGO_CLASS} />
  ),
  gamma: <GammaLogo role='img' aria-label='Gamma' className={LOGO_CLASS} />,
  graphite: (
    <GraphiteLogo role='img' aria-label='Graphite' className={LOGO_CLASS} />
  ),
  // DS brand mark; srText='' + aria-label matches the other logos' labelling.
  openai: (
    <LogoOpenAI
      srText=''
      role='img'
      aria-hidden={false}
      aria-label='OpenAI'
      className={LOGO_CLASS}
    />
  ),
  weightsAndBiases: (
    <WandbLogo role='img' aria-label='W&B' className={LOGO_CLASS} />
  ),
  micro1: <Micro1Logo role='img' aria-label='micro1' className={LOGO_CLASS} />,
}

const PartnerTile = ({
  id,
  extraClassName = '',
  ariaHidden = false,
}: {
  id: string
  extraClassName?: string
  ariaHidden?: boolean
}) => (
  <li
    aria-hidden={ariaHidden || undefined}
    className={`relative flex h-12 w-[112px] shrink-0 items-center justify-center ${extraClassName}`}
  >
    {LOGOS_BY_ID[id]}
  </li>
)

/**
 * Partner logo strip. Desktop: logos spread across the width. Below that: a
 * continuous marquee, or a manual scroll row under reduced motion.
 */
export const AiLabsPartners = ({ headline }: AiLabsPartnersProps) => {
  const prefersReducedMotion = useReducedMotion()

  return (
    <div className='flex flex-col gap-12'>
      <div className='flex flex-col gap-4'>
        <div className='border-t border-edu-fg-dimmed' />
        <p className='m-0 text-title-3 leading-title-3 font-semibold text-edu-fg-primary'>
          {headline}
        </p>
      </div>

      {/* Desktop: static, spread across the width. */}
      <ul className='hidden list-none items-center justify-between p-0 min-[1160px]:flex'>
        {AI_LABS_PARTNERS.map((partner) => (
          <PartnerTile key={partner.id} id={partner.id} />
        ))}
      </ul>

      {/* Mobile + tablet: continuous marquee (or a manual scroll row if reduced motion). */}
      <div className='-mx-5 overflow-hidden md:mx-0 min-[1160px]:hidden'>
        {prefersReducedMotion ? (
          <ul className='flex list-none items-center gap-x-10 overflow-x-auto px-5 scroll-px-5 p-0 [scrollbar-width:none] md:px-0 md:scroll-px-0 [&::-webkit-scrollbar]:hidden'>
            {AI_LABS_PARTNERS.map((partner) => (
              <PartnerTile key={partner.id} id={partner.id} />
            ))}
          </ul>
        ) : (
          // Duplicate set + trailing margin per tile → seamless -50% loop.
          <motion.ul
            className='flex w-max list-none items-center p-0'
            animate={{ x: ['0%', '-50%'] }}
            transition={{
              duration: MARQUEE_DURATION_S,
              ease: 'linear',
              repeat: Infinity,
            }}
          >
            {AI_LABS_PARTNERS.map((partner) => (
              <PartnerTile
                key={`a-${partner.id}`}
                id={partner.id}
                extraClassName='mr-10'
              />
            ))}
            {AI_LABS_PARTNERS.map((partner) => (
              <PartnerTile
                key={`b-${partner.id}`}
                id={partner.id}
                extraClassName='mr-10'
                ariaHidden
              />
            ))}
          </motion.ul>
        )}
      </div>
    </div>
  )
}
